import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { Bubble } from 'react-chartjs-2';
import { Chart as ChartJS, LinearScale, PointElement, Tooltip, Legend } from 'chart.js';
import {
  getSelectedAssessmentPeriodId,
  getPreviousAssessmentPeriodId,
} from '../../../common/selectors/auth';

import { hexToRgb } from 'common/styles/colors';

const BubbleChart = ({ bubbleChartData }) => {
  ChartJS.register(LinearScale, PointElement, Tooltip, Legend);
  const selectedAssessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const previousAssessmentPeriodId = useSelector(getPreviousAssessmentPeriodId);

  const [yAxisLimits, setYAxisLimits] = useState({ min: -15, max: 15 });
  const [chartData, setChartData] = useState({
    datasets: [
      {
        label: '',
        data: [],
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        borderColor: 'rgba(0, 0, 0, 1)',
        borderWidth: 1,
      },
    ],
  });

  useEffect(() => {
    const previousData = {};
    let currentMaxPercent = 0;
    let currentMinPercent = 0;

    if (bubbleChartData) {
      const previousAPIndex = bubbleChartData.findIndex(
        item => item.id === previousAssessmentPeriodId
      );
      const currentAPIndex = bubbleChartData.findIndex(
        item => item.id === selectedAssessmentPeriodId
      );

      if (previousAPIndex !== -1 && bubbleChartData[previousAPIndex]?.data) {
        bubbleChartData[previousAPIndex].data.forEach(item => {
          const { option_name, overall_score } = item;

          if (!previousData[option_name]) {
            previousData[option_name] = [];
          }
          previousData[option_name].push(overall_score);
        });
      }

      if (currentAPIndex !== -1 && bubbleChartData[currentAPIndex]?.data) {
        const groupedData = {};

        bubbleChartData[currentAPIndex].data.forEach(item => {
          const { option_name, overall_score, color, average_n } = item;

          if (!groupedData[option_name]) {
            groupedData[option_name] = [];
          }

          groupedData[option_name].push(overall_score);
          groupedData[option_name].lineColor = color;
          groupedData[option_name].average_n = average_n;
        });

        const formattedData = Object.keys(groupedData).map(optionName => {
          const currentValue = parseFloat(groupedData[optionName][0].toFixed(1));
          const previousValue = previousData[optionName]?.[0];

          const percentChange =
            previousValue !== undefined && previousValue !== 0
              ? parseFloat(((currentValue - previousValue) / previousValue) * 100).toFixed(1)
              : 0;

          const numericChange = isNaN(percentChange) ? 0 : Number(percentChange);
          currentMaxPercent = Math.max(currentMaxPercent, numericChange);
          currentMinPercent = Math.min(currentMinPercent, numericChange);

          return {
            label: optionName,
            data: [
              {
                x: currentValue,
                y: numericChange,
                r: groupedData[optionName].average_n,
              },
            ],
            borderColor: hexToRgb(groupedData[optionName].lineColor, 1.0),
            backgroundColor: hexToRgb(groupedData[optionName].lineColor, 0.6),
            borderWidth: 1,
          };
        });

        const padding = 10;
        const maxAbsolutePercent = Math.max(
          Math.abs(currentMaxPercent),
          Math.abs(currentMinPercent),
          0
        );
        const symmetricExtent = Math.round(maxAbsolutePercent + padding);

        setYAxisLimits({
          min: -symmetricExtent,
          max: symmetricExtent,
        });

        setChartData({
          datasets: formattedData,
        });
      }
    }
  }, [bubbleChartData, selectedAssessmentPeriodId, previousAssessmentPeriodId]);

  const getOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      autoPadding: false,
      padding: {
        top: 0,
      },
    },
    plugins: {
      legend: {
        position: 'bottom',
        display: true,
        labels: {
          font: {
            size: 12,
          },
          boxWidth: 10,
          boxHeight: 10,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: (Score: ${context.raw.x}, Change: ${context.raw.y > 0 ? '+' : ''
              }${context.raw.y}%, Members: ${context.raw.r})`;
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 10,
        title: {
          display: false,
          text: 'Score',
        },
      },
      y: {
        beginAtZero: false,
        min: yAxisLimits.min,
        max: yAxisLimits.max,
        title: {
          display: true,
          text: '% Change in Score',
          font: {
            family: 'Poppins',
            size: 11,
          },
        },
        grid: {
          drawBorder: true,
          color: context =>
            context.tick.value === 0 ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)',
          lineWidth: context => (context.tick.value === 0 ? 2 : 1),
        },
      },
    },
  });

  return (
    <div style={{ height: '470px', width: '100%' }}>
      <Bubble data={chartData} options={getOptions()} />
    </div>
  );
};
export default BubbleChart;
